import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import type { inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import { Link } from "react-router";

import type { AppRouter } from "@runcheetah/api/router";
import {
  CodeText,
  DataTable,
  PageHeader,
  QueryErrorState,
  SortableTableHeader,
  TableSkeleton,
} from "@/components/common";
import { getWorkspaceBillingStatusMeta } from "@/lib/billing-status";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type WorkspaceListItem = RouterOutputs["admin"]["workspaces"]["list"][number];

const formatDate = formatDateTime;

function WorkspaceBillingStatusPill({ status }: { status: WorkspaceListItem["billingStatus"] }) {
  if (!status) {
    return (
      <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
        Unassigned
      </span>
    );
  }

  const statusMeta = getWorkspaceBillingStatusMeta(status);

  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusMeta.className}`}
    >
      {statusMeta.label}
    </span>
  );
}

const columns: ColumnDef<WorkspaceListItem>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableTableHeader column={column}>Workspace</SortableTableHeader>,
    cell: ({ row }) => (
      <div className="min-w-0">
        <Link
          to={`/workspaces/${row.original.id}`}
          className="font-medium text-foreground hover:underline"
        >
          {row.original.name}
        </Link>
        <div className="mt-1 text-xs text-muted-foreground">{row.original.ownerEmail}</div>
      </div>
    ),
  },
  {
    accessorKey: "planName",
    header: "Plan",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.planName ?? "No plan"}</span>
    ),
  },
  {
    accessorKey: "memberCount",
    header: "Members",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.memberCount}</span>
    ),
  },
  {
    accessorKey: "brandCount",
    header: "Brands",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.brandCount}</span>
    ),
  },
  {
    accessorKey: "billingStatus",
    header: "Billing",
    cell: ({ row }) => <WorkspaceBillingStatusPill status={row.original.billingStatus} />,
  },
  {
    accessorKey: "updatedAt",
    header: ({ column }) => <SortableTableHeader column={column}>Last Updated</SortableTableHeader>,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{formatDate(row.original.updatedAt)}</span>
    ),
  },
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ row }) => <CodeText>{row.original.id}</CodeText>,
  },
];

export function WorkspacesTable({ data }: { data: WorkspaceListItem[] }) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "updatedAt", desc: true }]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable table={table} columnsCount={columns.length} emptyMessage="No workspaces found." />
  );
}

export function WorkspacesPage() {
  const { data, isPending, error } = trpc.admin.workspaces.list.useQuery({});

  if (isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="Workspaces" meta="Loading..." />
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        title="Failed to load workspaces"
        message={error.message}
        isForbidden={error.data?.code === "FORBIDDEN"}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Workspaces"
        meta={`${data.length} ${data.length === 1 ? "workspace" : "workspaces"}`}
        description="Review workspace ownership, linked brands, and beta billing plan assignments."
      />
      <WorkspacesTable data={data} />
    </div>
  );
}
